import { useState, FormEvent } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
} from "@mui/material";
import { useAuth } from "../../../context/AuthContext";
import { updatePassword, updateUser } from "../../../utils/helper";
import { PasswordChangeRequest } from "../../../utils/types";

export default function SettingsGrid() {
  const { username, email, setEmail, id, jwt } = useAuth();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [profileMessage, setProfileMessage] = useState("");

  const handleProfileUpdate = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordMessage("");
    setProfileMessage("");

    const user = { email };

    try {
      const result = await updateUser(id, user, jwt);
      if (result) {
        setProfileMessage("Profile updated!");
      }
    } catch (error: any) {
      setProfileMessage(
        error?.response?.data?.message || "Failed to update profile."
      );
    }
  };

  const handlePasswordChange = async (e: FormEvent) => {
    e.preventDefault();
    setPasswordMessage("");
    setProfileMessage("");

    if (newPassword !== confirmPassword) {
      setPasswordMessage("Passwords do not match.");
      return;
    }

    const passwordChangeRequest: PasswordChangeRequest = {
      currentPassword,
      newPassword,
    };

    try {
      await updatePassword(id, passwordChangeRequest, jwt);
      setPasswordMessage("Password updated!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      setPasswordMessage(
        error?.response?.data?.message || "Failed to update password."
      );
    }
  };

  return (
    <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", mt: 6 }}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Account Settings
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Username
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {username}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {email}
              </Typography>
            </Box>
            <form onSubmit={handleProfileUpdate}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Update Email
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  fullWidth
                  type="email"
                />
                <Button type="submit" variant="contained">
                  Update Profile
                </Button>
                {profileMessage && (
                  <Typography color="success.main">{profileMessage}</Typography>
                )}
              </Stack>
            </form>
            <form onSubmit={handlePasswordChange}>
              <Typography variant="subtitle1" sx={{ mb: 1, pt: 2 }}>
                Change Password
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="Current Password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="New Password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  fullWidth
                />
                <TextField
                  label="Confirm New Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  fullWidth
                />
                <Button type="submit" variant="contained" sx={{ pt: 2 }}>
                  Change Password
                </Button>
                {passwordMessage && (
                  <Typography
                    color={
                      passwordMessage.includes("not match")
                        ? "error.main"
                        : "success.main"
                    }
                  >
                    {passwordMessage}
                  </Typography>
                )}
              </Stack>
            </form>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
